import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { ContextualMenu, Persona, PersonaSize, PersonaInitialsColor, IContextualMenuItem } from '@fluentui/react';

import styles from "./UserInfoMenu.module.css";

import { UserData, logout, getUserPhoto } from '../../api'
import { getToken } from "../../authConfig";
import { handleError } from "../../toaster";

interface Props {
    userData: UserData;
}

export const UserInfoMenu = ({ userData }: Props) => {
    const { instance } = useMsal();
    const client = useMsal().instance;
    const activeAccount = instance.getActiveAccount();
    const [showContextMenu, setShowContextMenu] = useState(false);
    const [userPhoto, setUserPhoto] = useState<string | undefined>();
    const personaRef = useRef(null);

    const onPersonaClick = (ev: React.MouseEvent<HTMLElement>) => {
        ev.preventDefault();
        setShowContextMenu(true);
    };

    const onHideContextMenu = () => {
        setShowContextMenu(false);
    };

    const handleLogout = () => {
        if (activeAccount) {
            instance
                .logoutRedirect({
                    postLogoutRedirectUri: "/logout",
                    account: instance.getActiveAccount()
                })
                .catch(error => handleError(error, "An unknown error occurred"));
        } else {
            logout();
        }
    };

    const handleCopyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        onHideContextMenu();
    };

    const menuItems: IContextualMenuItem[] = [
        {
            key: 'user',
            iconProps: { iconName: 'AccountManagement' },
            text: `${userData.displayName}`,
            className: styles.menuItem
        },
        {
            key: 'copySessionInfoToClipboard',
            iconProps: { iconName: 'Copy' },
            text: 'Copy Session Info to Clipboard',
            onClick: () => {
                const { user_id, userPrincipalName, session_id } = userData;
                const message = `User ID: ${user_id}\nUsername: ${userPrincipalName}\nSession ID: ${session_id}`;
                handleCopyToClipboard(message);
            },
        },
        {
            key: 'logout',
            iconProps: { iconName: 'SignOut' },
            text: 'Logout',
            onClick: () => handleLogout(),
            className: styles.menuItem
        },
    ];

    useEffect(() => {
        const fetchUserPhoto = async () => {
            try {
                const token = client ? await getToken(client) : undefined;
                const photoUrl = await getUserPhoto(token);
                setUserPhoto(photoUrl);
            }
            catch (error) {
                handleError(error, "Error fetching user photo")
            }
        };
        fetchUserPhoto();
    }, [userData]);

    return (
        <div>
            {userData && (
                <>
                    <Persona
                        className={styles.persona}
                        imageUrl={userPhoto ?? undefined}
                        size={PersonaSize.size48}
                        initialsColor={PersonaInitialsColor.blue}
                        onClick={onPersonaClick}
                        ref={personaRef}
                    />
                    <ContextualMenu
                        items={menuItems}
                        hidden={!showContextMenu}
                        target={personaRef}
                        onItemClick={onHideContextMenu}
                        onDismiss={onHideContextMenu}
                    />
                </>
            )}
        </div>
    );
};